/**
 * Yantra MUI theme — global palette applied to every MUI component
 * (Buttons, Cards, Tables, TextFields, Chips, Switches, etc.).
 *
 * Two modes:
 *   light → default
 *   dark  → activated by setting <body data-theme="dark"> (the sidebar
 *           toggle does this; App.js mirrors it into MUI via this factory)
 */
import { createTheme } from '@mui/material/styles';

const YANTRA_ORANGE        = '#ec6e17';
const YANTRA_ORANGE_DARK   = '#c5560a';
const YANTRA_ORANGE_LIGHT  = '#fff7ed';
const YANTRA_BLUE          = '#2563eb';

export const getYantraTheme = (mode = 'light') => createTheme({
  palette: {
    mode,
    primary: {
      main: YANTRA_ORANGE,
      dark: YANTRA_ORANGE_DARK,
      light: YANTRA_ORANGE_LIGHT,
      contrastText: '#fff',
    },
    secondary: {
      main: YANTRA_BLUE,
      dark: '#1d4ed8',
      light: '#dbeafe',
      contrastText: '#fff',
    },
    error:   { main: '#b91c1c', light: '#fef2f2', dark: '#991b1b' },
    warning: { main: '#ca8a04', light: '#fef9c3', dark: '#854d0e' },
    success: { main: '#0f766e', light: '#d1fae5', dark: '#065f46' },
    ...(mode === 'dark'
      ? {
          background: { default: '#0f172a', paper: '#1e293b' },
          text: { primary: '#f1f5f9', secondary: '#cbd5e1', disabled: '#64748b' },
          divider: '#334155',
          action: {
            hover: 'rgba(255, 255, 255, 0.04)',
            selected: 'rgba(236, 110, 23, 0.16)',
          },
        }
      : {
          background: { default: '#f8fafc', paper: '#ffffff' },
          text: { primary: '#1f2937', secondary: '#6b7280', disabled: '#9ca3af' },
          divider: '#e5e7eb',
          action: {
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(236, 110, 23, 0.08)',
          },
        }),
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { textTransform: 'none', fontWeight: 600 },
    body1: { fontSize: '14px' },
    body2: { fontSize: '13px' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8 },
        containedPrimary: {
          backgroundColor: YANTRA_ORANGE,
          '&:hover': { backgroundColor: YANTRA_ORANGE_DARK },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        outlined: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: YANTRA_ORANGE,
            '& + .MuiSwitch-track': { backgroundColor: YANTRA_ORANGE },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700 },
      },
    },
  },
});

export default getYantraTheme;
